import React, { BaseSyntheticEvent, useState } from 'react'
import {
  IonButton,
  IonButtons,
  IonChip,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonPage,
  IonRow,
  IonSearchbar,
  IonTitle,
  IonToggle,
  IonToolbar,
  useIonViewDidEnter,
  useIonViewWillLeave,
} from '@ionic/react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Virtuoso } from 'react-virtuoso'
import { chevronBackOutline, closeCircleOutline } from 'ionicons/icons'
import { useHistory } from 'react-router'
import { getCcHpiInjuryDetailsSelector } from '../../selectors/injuryDetails.selector'
import { getCcHpiOptionsSelector } from '../../../options/selectors'
import { setInjurycause } from '../../store/ccHpi.actions'
import { TabConstants } from '../../../../components/tabs/Tabs.constants'
import { IOption, IOptions } from '../../../../common/common.types'
import PCRHeader from '../../../../components/pcrHeader/PCRHeader.component'
import { initializeSearch } from '../../../../components/search/store/search.actions'

const InjuryCause: React.FC = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const dispatch = useDispatch()
  const injuryDetailsState = useSelector(getCcHpiInjuryDetailsSelector)
  const { injurycause } = injuryDetailsState
  const cchpiOptionsState = useSelector(getCcHpiOptionsSelector)
  const { injurycauseOptions } = cchpiOptionsState
  const [options, setOptions] = useState<IOptions>([])
  const [searchValue, setSearchValue] = useState<string>('')

  useIonViewWillLeave(() => {
    dispatch(initializeSearch([]))
  })

  useIonViewDidEnter(() => {
    if (injurycauseOptions) {
      const chunk = injurycauseOptions.slice(0, 20)
      setOptions([...chunk])
    } else {
      setOptions([])
    }
    setSearchValue('')
  }, [injurycauseOptions])

  const onSelect = (e: BaseSyntheticEvent, option: IOption) => {
    e.preventDefault()
    const updatedInjuryCause: IOptions = [...injurycause]
    if (e.target.checked) {
      updatedInjuryCause.push(option)
    } else {
      const index = updatedInjuryCause.findIndex((opt: IOption) => opt.id === option.id)
      if (index !== -1) {
        updatedInjuryCause.splice(index, 1)
      }
    }
    dispatch(setInjurycause(updatedInjuryCause))
  }

  const onBackClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    history.push(TabConstants.CC_HPI)
  }

  const handleSearch = (e: CustomEvent) => {
    setSearchValue(e.detail.value)
    if (e.detail.value === '') {
      setOptions(injurycauseOptions)
    } else {
      let filteredOptionsList: IOptions = []
      if (injurycauseOptions) {
        filteredOptionsList = injurycauseOptions.filter((o: IOption) =>
          o.name?.toLowerCase().includes(e.detail.value.toLowerCase().trim())
        )
        setOptions(filteredOptionsList)
      }
    }
  }

  const removeInjuryCauseChip = (option: IOption) => {
    const filteredOptions = injurycause.filter((op: IOption) => op.id !== option.id)
    dispatch(setInjurycause(filteredOptions))
  }

  const loadData = () => {
    if (searchValue === '') {
      if (injurycauseOptions.length !== options.length) {
        const chunk = injurycauseOptions.slice(options.length, options.length + 20)
        setOptions([...options, ...chunk])
      }
    }
  }

  return (
    <IonPage id='InjuryCause'>
      <PCRHeader />
      <IonHeader className='ion-no-border'>
        <IonToolbar color='light'>
          <IonButtons slot='start'>
            <IonButton data-testid='Back' fill='clear' onClick={onBackClick} color='primary'>
              <IonIcon icon={chevronBackOutline} />
              {t('common.buttons.Back')}
            </IonButton>
          </IonButtons>
          <IonTitle>{t('ccHpi.injurycause')}</IonTitle>
        </IonToolbar>
        {injurycause.length > 0 && (
          <IonItem lines='none'>
            <IonRow className='ion-nowrap no-flex-shrink' id='List-Row'>
              {injurycause.map((option: IOption) => (
                <IonChip
                  data-testid={`Chip${option.id}`}
                  key={`Chip_${option.id}${option.name}`}
                  className='lookup-search-chips custom-background'>
                  <IonLabel>{option.name}</IonLabel>
                  <IonIcon color='white' icon={closeCircleOutline} onClick={() => removeInjuryCauseChip(option)} />
                </IonChip>
              ))}
            </IonRow>
          </IonItem>
        )}
        <IonToolbar color='light'>
          <IonSearchbar
            data-testid='SearchBar'
            debounce={0}
            value={searchValue}
            onIonChange={handleSearch}
            autocomplete='off'
          />
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        {searchValue && options?.length === 0 && (
          <IonItem key={'no_options_found'}>
            <IonLabel>
              {t('common.NoSearchResults')}
              {`"${searchValue}"`}
            </IonLabel>
          </IonItem>
        )}
        {options?.length > 0 && (
          <Virtuoso
            data={options}
            increaseViewportBy={{ top: 500, bottom: 500 }}
            overscan={10}
            endReached={loadData}
            style={{ height: '100%' }}
            itemContent={(index, option: IOption) => {
              return (
                <div style={{ minHeight: '44px' }} className='webkit-line-clamp-2'>
                  <IonItem key={`Option_${option.id}`} data-skip-auto-enhance>
                    <IonLabel>{option.name}</IonLabel>
                    <IonToggle
                      slot='start'
                      name={option.name}
                      value={`${option.id}`}
                      checked={injurycause.filter((o) => o.id === option.id).length > 0}
                      onClick={(e) => onSelect(e, option)}
                      color={'zoll-blue-secondary'}
                    />
                  </IonItem>
                </div>
              )
            }}
          />
        )}
      </IonContent>
    </IonPage>
  )
}

export default InjuryCause
